import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/ui/Badge';
import { PageSkeleton } from '@/components/ui/Skeleton';
import type { Account } from '@/lib/api-types';

type TabItem = { to: string; label: string; end?: boolean };

export function AccountLayout() {
  const { id } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);

  const { data: account, isLoading } = useQuery<Account>({
    queryKey: ['account', id],
    queryFn: async () => (await api.get(`/accounts/${id}`)).data.data,
    enabled: Boolean(id),
  });

  const primaryTabs: TabItem[] = useMemo(
    () => [
      { to: `/accounts/${id}`, label: 'Przegląd', end: true },
      { to: `/accounts/${id}/campaign-wizard`, label: 'Kreator kampanii' },
      { to: `/accounts/${id}/diagnostics`, label: t('nav.diagnostics') },
      { to: `/accounts/${id}/insights`, label: 'Insights' },
      { to: `/accounts/${id}/recommendations`, label: 'Rekomendacje' },
      { to: `/accounts/${id}/experiments`, label: 'Eksperymenty' },
      { to: `/accounts/${id}/audiences`, label: 'Audience' },
      { to: `/accounts/${id}/feed-optimizer`, label: 'Feed Optimizer' },
      { to: `/accounts/${id}/mutations`, label: t('nav.mutations') },
      { to: `/accounts/${id}/chat`, label: t('nav.chat') },
      { to: `/accounts/${id}/reports`, label: t('nav.reports') },
    ],
    [id, t],
  );

  const moreMenuItems: TabItem[] = useMemo(
    () => [
      { to: `/accounts/${id}/conversion-actions`, label: 'Akcje konwersji' },
      { to: `/accounts/${id}/conversion-uploads`, label: 'Konwersje offline' },
      { to: `/accounts/${id}/conversion-value-rules`, label: 'Reguły wartości' },
      { to: `/accounts/${id}/lifecycle-goals`, label: 'Lifecycle Goals' },
      { to: `/accounts/${id}/brand-guidelines`, label: 'Brand Guidelines' },
      { to: `/accounts/${id}/links`, label: 'Połączone konta' },
      { to: `/accounts/${id}/scheduled-reports`, label: 'Harmonogramy' },
      { to: `/accounts/${id}/change-events`, label: 'Historia zmian' },
      { to: `/accounts/${id}/reach-forecast`, label: 'Reach Forecast' },
    ],
    [id],
  );

  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!moreRef.current) return;
      if (!moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  if (isLoading && !account) return <PageSkeleton />;

  const isTabActive = (tab: TabItem) =>
    tab.end ? location.pathname === tab.to : location.pathname.startsWith(tab.to);
  const moreActive = moreMenuItems.some((m) => location.pathname.startsWith(m.to));

  return (
    <div className="container py-6">
      {/* Header */}
      {account && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-lg font-bold shadow-md shrink-0">
              {account.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold dark:text-white truncate">
                {account.name}
              </h1>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                <span className="text-sm text-slate-500 font-mono">
                  {account.customer_id}
                </span>
                <StatusBadge status={account.status} />
                {account.vertical && (
                  <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full">
                    {account.vertical}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary flex items-center gap-2">
              <RefreshCw size={14} />
              {t('accounts.sync_now')}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
        {primaryTabs.map((tab) => {
          const active = isTabActive(tab);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                active
                  ? 'text-brand-700 dark:text-brand-400 border-brand-500'
                  : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600',
              )}
            >
              {tab.label}
            </Link>
          );
        })}
        <div className="relative" ref={moreRef}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            className={cn(
              'px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1',
              moreActive || moreOpen
                ? 'text-brand-700 dark:text-brand-400 border-brand-500'
                : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            Więcej
            <ChevronDown
              size={14}
              className={cn('transition-transform', moreOpen && 'rotate-180')}
            />
          </button>
          {moreOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-1 w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 py-1"
            >
              {moreMenuItems.map((item) => {
                const active = location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    onClick={() => setMoreOpen(false)}
                    className={cn(
                      'block px-4 py-2 text-sm transition-colors',
                      active
                        ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800',
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      <Outlet />
    </div>
  );
}
